import { Input, Select } from '@aigc/antd';
import { NonEmpty, VideoThumbnail } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import type { Pagination } from '@aigc/types';
import {
  EyeOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Form,
  Grid,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useCallback, useImperativeHandle, useMemo, type Ref } from 'react';
import { useForm } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { array, number, object, string, tuple, type InferType } from 'yup';
import { FormControl } from '../../../components/form-control';
import { useSetFrameImagePageData } from '../../../features/frame-image/store';
import { useSetReferenceImagePageData } from '../../../features/reference-image/store';
import { ScriptSourceTag } from '../../../features/task/components/script-source-tag';
import { TaskStatus } from '../../../features/task/components/task-status';
import {
  TaskType,
  type VideoGenerationTaskDto,
  type VideoTaskDto,
} from '../../../features/task/model';
import { VideoCredit } from '../../credit/components/video-credit';
import { MediaPreviewButton } from '../../file/components/media-preview-button';
import { GenerationType, videoModelSpecs } from '../../video-model/model-spec';
import { VideoTranslationOptionTags } from '../../video-translation/components/video-translation-option-tags';
import {
  getAdminVideoGenerationTasksQueryOptions,
  getVideoGenerationTasksQueryOptions,
} from '../../video/api';
import { ScriptSource } from '../../video/model';
import { todayRange } from '../constants';
import { TimeRangePicker } from './time-range-picker';
import { VideoGenerationDetailDrawer } from './video-generation-detail-drawer';
import { VideoTranslationLanguageTag } from './video-translation-language-tag';
import { VideoTranslationSceneTypeTag } from './video-translation-scene-type-tag';

const querySchema = object({
  id: string().trim().nullable(),
  status: array(string().required()).nullable(),
  source: number<ScriptSource>().nullable(),
  product_name: string().nullable(),
  task_type: array(string<TaskType>().required()).nullable(),
  ilike_account_name: string().nullable(),
  dna: string().nullable(),
  time_range: tuple([number().required(), number().required()]).required(),
});

type QueryFormValues = InferType<typeof querySchema>;
type QueryParams = Omit<QueryFormValues, 'time_range'> & {
  start_time?: number;
  end_time?: number;
};

const defaultTaskTypes = [
  TaskType.视频生成,
  TaskType.视频翻译,
  TaskType.视频翻译_废弃,
  TaskType.长视频生成,
  TaskType.智能擦除,
  TaskType.视频编辑,
  TaskType.画质修复,
];

export const VideoHistory = (props: {
  ref?: Ref<{ refresh: () => void }>;
  admin?: boolean;
  defaultPagination?: Partial<Pagination>;
  onPaginationChange?: (pagination: Pagination) => void;
}) => {
  const screen = Grid.useBreakpoint();
  const navigate = useNavigate();
  const [openOverlay] = useOverlay();
  const setFrameImagePageData = useSetFrameImagePageData();
  const setReferenceImagePageData = useSetReferenceImagePageData(false);
  const setLongReferenceImagePageData = useSetReferenceImagePageData(true);
  const showTimeRange = props.admin ?? false;

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(querySchema),
    defaultValues: {
      status: null,
      source: null,
      time_range: [...todayRange],
    },
  });

  const { pagination, queryMany, queryParams, changePageNumber } =
    usePagingQueryHelper<QueryParams>({
      defaultPagination: {
        page: props.defaultPagination?.page ?? 1,
        size: props.defaultPagination?.size ?? 10,
      },
      defaultQueryParams: {
        id: null,
        status: null,
        source: null,
        product_name: null,
        task_type: null as TaskType[] | null,
        dna: null,
        start_time: showTimeRange ? todayRange[0] : undefined,
        end_time: showTimeRange ? todayRange[1] : undefined,
      },
      onRefresh: () => {
        refetch();
      },
    });

  const historyParams = {
    status: queryParams?.status?.length ? queryParams.status : undefined,
    task_type: (() => {
      const types = queryParams?.task_type?.length
        ? queryParams.task_type
        : defaultTaskTypes;
      return types.includes(TaskType.视频翻译) &&
        !types.includes(TaskType.视频翻译_废弃)
        ? [...types, TaskType.视频翻译_废弃]
        : types;
    })(),
    product_name: queryParams?.product_name ?? undefined,
    dna: queryParams?.dna ?? undefined,
  };

  const {
    data: history,
    isFetching,
    refetch,
  } = useQuery({
    ...(props.admin
      ? getAdminVideoGenerationTasksQueryOptions(pagination, {
          ...historyParams,
          source: queryParams?.source ?? undefined,
          id: queryParams?.id || undefined,
          start_time: queryParams?.start_time,
          end_time: queryParams?.end_time,
          ilike_account_name: queryParams?.ilike_account_name ?? undefined,
        })
      : getVideoGenerationTasksQueryOptions(pagination, historyParams)),
    placeholderData: keepPreviousData,
  });

  useImperativeHandle(props.ref, () => ({
    refresh: () => refetch(),
  }));

  const onViewScript = useCallback(
    (task: VideoGenerationTaskDto) => {
      match(task)
        .with(
          {
            type: TaskType.视频生成,
            generate_data: P.select({
              generation_type: GenerationType.Frame,
            }),
          },
          (data) => {
            setFrameImagePageData({
              simple_mode: true,
              ...data,
            });
            navigate({ to: '/video/create' });
          },
        )
        .with(
          {
            type: TaskType.视频生成,
            generate_data: P.select({
              generation_type: GenerationType.Reference,
            }),
          },
          (data) => {
            setReferenceImagePageData({
              simple_mode: true,
              ...data,
            });
            navigate({
              to: '/video/reference-create',
            });
          },
        )
        .with(
          {
            type: TaskType.长视频生成,
            generate_data: P.select({
              generation_type: GenerationType.Reference,
            }),
          },
          (data) => {
            setLongReferenceImagePageData({
              simple_mode: true,
              ...data,
            });
            navigate({
              to: '/video/long/reference-create',
            });
          },
        );
    },
    [
      navigate,
      setFrameImagePageData,
      setReferenceImagePageData,
      setLongReferenceImagePageData,
    ],
  );

  const columns = useMemo(() => {
    const renderScriptButton = (task: VideoTaskDto) => {
      return match(task)
        .with(
          {
            type: P.union(TaskType.视频生成, TaskType.长视频生成),
          },
          (data) => (
            <Button
              block
              variant="filled"
              color="default"
              icon={<EyeOutlined />}
              onClick={() => {
                openOverlay(
                  <VideoGenerationDetailDrawer
                    task={data}
                    onGenerate={onViewScript}
                  />,
                );
              }}
            >
              查看脚本
            </Button>
          ),
        )
        .otherwise(() => null);
    };

    const _columns: TableColumnsType<VideoTaskDto> = [
      {
        className: '!pr-0',
        width: 96,
        render: (_, row) => {
          if (row.status !== 'succeeded' && row.status !== 'failed') {
            return (
              <Skeleton.Image classNames={{ content: '!w-20 !h-20' }} active />
            );
          }

          if (!row.filename || row.status === 'failed') {
            return <Skeleton.Image classNames={{ content: '!w-20 !h-20' }} />;
          }

          return (
            <VideoThumbnail
              size={80}
              src={row.filename}
              classNames={{ image: 'rounded' }}
            ></VideoThumbnail>
          );
        },
      },
      {
        render: (_, task) => {
          return (
            <div className="space-y-2">
              <div className="mb-2 flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                <Tag># {task.id}</Tag>
                {match(task)
                  .with(
                    {
                      type: P.union(TaskType.视频生成, TaskType.长视频生成),
                    },
                    (videoTask) => (
                      <ScriptSourceTag
                        source={videoTask.generate_data?.source}
                      ></ScriptSourceTag>
                    ),
                  )
                  .otherwise(() => null)}
                {match(task)
                  .with(
                    {
                      type: P.not(TaskType.智能擦除),
                      generate_data: P.select(),
                    },
                    (data) => (
                      <span className="text-base">
                        <NonEmpty
                          value={data?.product_name}
                          render={(value) => (
                            <>
                              产品名称：
                              {value}
                            </>
                          )}
                        ></NonEmpty>
                      </span>
                    ),
                  )
                  .otherwise(() => null)}
                {match(task)
                  .with({ type: TaskType.视频翻译 }, (translationTask) => {
                    return (
                      <VideoTranslationSceneTypeTag
                        needs_translation={
                          translationTask.generate_data?.precheck
                            ?.needs_translation
                        }
                        sceneType={
                          translationTask.generate_data?.precheck
                            ?.scene_classification?.scene_type
                        }
                      />
                    );
                  })
                  .otherwise(() => null)}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <TaskStatus task={task}></TaskStatus>
                {match(task.type)
                  .with(
                    P.union(TaskType.视频翻译, TaskType.视频翻译_废弃),
                    () => <Tag>翻译</Tag>,
                  )
                  .with(TaskType.智能擦除, () => <Tag>智能擦除</Tag>)
                  .with(TaskType.视频编辑, () => <Tag>视频编辑</Tag>)
                  .with(TaskType.画质修复, () => <Tag>画质修复</Tag>)
                  .with(P.union(TaskType.视频生成, TaskType.长视频生成), () => (
                    <Tag>视频生成</Tag>
                  ))
                  .otherwise(() => null)}
                {match(task)
                  .with(
                    {
                      type: P.union(TaskType.视频翻译),
                    },
                    (translationTask) => (
                      <>
                        <VideoTranslationLanguageTag
                          source={
                            translationTask.generate_data?.precheck
                              ?.audio_language
                          }
                          target={
                            translationTask.generate_data?.target_language
                          }
                        />
                        <VideoTranslationOptionTags
                          options={translationTask.generate_data}
                        />
                      </>
                    ),
                  )
                  .otherwise(() => null)}
                <NonEmpty
                  value={task.dna}
                  render={(dna) => (
                    <Typography.Text
                      classNames={{ root: '!text-xs' }}
                      copyable={{
                        text: () => {
                          return `[${task.dna}]`;
                        },
                      }}
                    >
                      <Tag>DNA: {dna}</Tag>
                    </Typography.Text>
                  )}
                ></NonEmpty>
                {match(task)
                  .with(
                    {
                      type: P.union(TaskType.视频生成, TaskType.长视频生成),
                    },
                    (value) =>
                      match(value)
                        .with(
                          {
                            generate_data: {
                              generation_type: GenerationType.Frame,
                            },
                          },
                          () => <Tag>首尾帧生成</Tag>,
                        )
                        .with(
                          {
                            generate_data: {
                              generation_type: GenerationType.Reference,
                            },
                          },
                          () => <Tag>参考图生成</Tag>,
                        )
                        .otherwise(() => null),
                  )
                  .otherwise(() => null)}
                {props.admin && task.creator?.name && (
                  <Tag icon={<UserOutlined />}>{task.creator.name}</Tag>
                )}
                <VideoCredit task={task}></VideoCredit>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <NonEmpty
                  value={task.duration}
                  render={(value) => (
                    <span key="duration">视频时长：{value}s</span>
                  )}
                ></NonEmpty>
                {match(task)
                  .with(
                    {
                      type: P.not(
                        P.union(
                          TaskType.智能擦除,
                          TaskType.视频翻译,
                          TaskType.画质修复,
                          TaskType.视频翻译_废弃,
                        ),
                      ),
                      generate_data: P.select(P.nonNullable),
                    },
                    (data) => (
                      <NonEmpty
                        value={data.aspect_ratio}
                        render={(value) => (
                          <span key="aspect_ratio">宽高比：{value}</span>
                        )}
                      ></NonEmpty>
                    ),
                  )
                  .otherwise(() => null)}
                {match(task)
                  .with(
                    {
                      type: P.not(
                        P.union(
                          TaskType.智能擦除,
                          TaskType.视频翻译,
                          TaskType.画质修复,
                          TaskType.视频翻译_废弃,
                        ),
                      ),
                    },
                    () => (
                      <NonEmpty
                        value={task.model}
                        render={(value) => (
                          <span key="model">
                            模型：{videoModelSpecs.get(value)?.name ?? value}
                          </span>
                        )}
                      ></NonEmpty>
                    ),
                  )
                  .otherwise(() => null)}
                <NonEmpty
                  value={task.created_at}
                  render={(value) => (
                    <span key="created_at">
                      创建时间： {new Date(value).toLocaleString()}
                    </span>
                  )}
                ></NonEmpty>
                {TaskStatus.isSettled(task.status) ? (
                  <span className="text-xs">
                    耗时：
                    {formatDuration(
                      intervalToDuration({
                        start: task.created_at,
                        end: task.updated_at ?? 0,
                      }),
                      { locale: zhCN },
                    )}
                  </span>
                ) : (
                  <span className="text-xs">
                    {formatDistanceToNow(task.created_at, {
                      locale: zhCN,
                      includeSeconds: true,
                      addSuffix: true,
                    })}
                  </span>
                )}
              </div>
              {task.error && TaskStatus.isFailed(task.status) && (
                <Alert
                  classNames={{ title: 'break-words break-all' }}
                  type="error"
                  title={<span>{task.error}</span>}
                ></Alert>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 sm:hidden">
                {renderScriptButton(task)}
                {TaskStatus.isSucceeded(task.status) && task.filename && (
                  <MediaPreviewButton
                    block
                    variant="filled"
                    color="primary"
                    href={task.filename}
                    icon={<VideoCameraOutlined />}
                  >
                    查看视频
                  </MediaPreviewButton>
                )}
              </div>
            </div>
          );
        },
      },
    ];

    if (screen.sm) {
      _columns.push({
        width: 136,
        render: (_, task) => {
          return (
            <div className="flex flex-col gap-2">
              {renderScriptButton(task)}
              {TaskStatus.isSucceeded(task.status) && task.filename && (
                <MediaPreviewButton
                  block
                  variant="filled"
                  color="primary"
                  href={task.filename}
                  icon={<VideoCameraOutlined />}
                >
                  查看视频
                </MediaPreviewButton>
              )}
            </div>
          );
        },
      });
    }

    return _columns;
  }, [onViewScript, openOverlay, props.admin, screen.sm]);

  const onQuery = handleSubmit((values) => {
    queryMany({
      id: props.admin ? values.id?.trim() || undefined : undefined,
      status: values.status?.length ? values.status : undefined,
      source: values.source ?? undefined,
      product_name: values.product_name ?? undefined,
      task_type: values.task_type ?? undefined,
      ilike_account_name: values.ilike_account_name,
      dna: values.dna,
      start_time: showTimeRange ? values.time_range[0] : undefined,
      end_time: showTimeRange ? values.time_range[1] : undefined,
    });
    props.onPaginationChange?.({ page: 1, size: pagination.size });
  });

  const handleReset = () => {
    const resetTimeRange: [number, number] = [...todayRange];
    reset();
    queryMany({
      id: undefined,
      status: undefined,
      source: undefined,
      product_name: undefined,
      task_type: undefined,
      ilike_account_name: undefined,
      dna: undefined,
      start_time: showTimeRange ? resetTimeRange[0] : undefined,
      end_time: showTimeRange ? resetTimeRange[1] : undefined,
    });
    props.onPaginationChange?.({ page: 1, size: pagination.size });
  };

  return (
    <Table
      rowKey={(row) => row.id}
      showHeader={false}
      loading={isFetching}
      dataSource={history?.items as VideoTaskDto[]}
      columns={columns}
      title={() => (
        <Form layout="inline" classNames={{ root: 'gap-y-4' }}>
          {props.admin && (
            <FormControl
              control={control}
              name="id"
              label="任务 ID"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} allowClear />
              )}
            />
          )}
          <FormControl
            control={control}
            name="product_name"
            label="产品名称"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''}></Input>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="dna"
            label="DNA"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''}></Input>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="task_type"
            label="任务类型"
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? []}
                mode="multiple"
                maxTagCount={2}
                allowClear
                options={[
                  { label: '视频生成', value: TaskType.视频生成 },
                  { label: '长视频生成', value: TaskType.长视频生成 },
                  { label: '视频编辑', value: TaskType.视频编辑 },
                  { label: '视频翻译', value: TaskType.视频翻译 },
                  { label: '智能擦除', value: TaskType.智能擦除 },
                  { label: '画质修复', value: TaskType.画质修复 },
                ]}
                className="min-w-36"
              ></Select>
            )}
          ></FormControl>
          {props.admin && (
            <FormControl
              control={control}
              name="source"
              label="来源"
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? undefined}
                  allowClear
                  options={[
                    { label: '爆款生成', value: ScriptSource.爆款生成 },
                    { label: '爆款复刻', value: ScriptSource.爆款复刻 },
                  ]}
                  className="min-w-32"
                />
              )}
            />
          )}
          <FormControl
            control={control}
            name="status"
            label="任务状态"
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? []}
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                options={[
                  { label: '未开始', value: 'not_started' },
                  { label: '运行中', value: 'running' },
                  { label: '已成功', value: 'succeeded' },
                  { label: '已失败', value: 'failed' },
                ]}
                className="min-w-32"
              />
            )}
          />
          {props.admin && (
            <FormControl
              control={control}
              name="ilike_account_name"
              label="账号名称"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''}></Input>
              )}
            ></FormControl>
          )}
          {showTimeRange && (
            <FormControl
              control={control}
              name="time_range"
              label="统计时间"
              render={({ field }) => (
                <TimeRangePicker
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
          <Space>
            <Button
              className="mr-2"
              onClick={onQuery}
              variant="solid"
              htmlType="submit"
              color="primary"
            >
              查询
            </Button>
            <Button variant="outlined" color="default" onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form>
      )}
      pagination={{
        total: history?.total,
        simple: screen.xs,
        current: history?.page ?? pagination.page,
        pageSize: history?.size ?? pagination.size,
        onChange: (page, pageSize) => {
          changePageNumber(page, pageSize);
          props.onPaginationChange?.({ page, size: pageSize });
        },
        showTotal: (total) => `共 ${total} 个视频`,
      }}
    ></Table>
  );
};
